/**
 * Master services agreement signing (issue #243).
 *
 * The contractor signs their MSA in the app, then staff countersign. The API
 * reports a contract's MSA as one of the statuses below. This lives in `lib/`
 * so the rules are testable without a component harness.
 */

export const MSA_AWAITING_SIGNATURE = 'awaiting-signature';
export const MSA_AWAITING_COUNTERSIGNATURE = 'awaiting-countersignature';
export const MSA_EXECUTED = 'executed';
export const MSA_RECORDED = 'recorded';
export const MSA_NO_CONTRACT = 'no-contract';

export const MSA_STATUS_LABELS = {
    [MSA_AWAITING_SIGNATURE]: 'Awaiting the contractor’s signature',
    [MSA_AWAITING_COUNTERSIGNATURE]: 'Awaiting countersignature',
    [MSA_EXECUTED]: 'Signed by both parties',
    [MSA_RECORDED]: 'Signed outside the app',
};

export const MSA_STATUS_COLORS = {
    [MSA_AWAITING_SIGNATURE]: 'bg-secondary',
    [MSA_AWAITING_COUNTERSIGNATURE]: 'bg-warning text-dark',
    [MSA_EXECUTED]: 'bg-success',
    [MSA_RECORDED]: 'bg-success',
};

/**
 * Whether the contractor must sign before anything else in the app is
 * unlocked. Only an unsigned agreement gates: once they have signed, they can
 * look around while Company countersigns (the API still blocks engagement).
 *
 * @param {string | undefined} status
 * @returns {boolean}
 */
export function msaGatePending(status) {
    return status === MSA_AWAITING_SIGNATURE;
}

/**
 * Whether the agreement is in force, so the contractor can be offered work.
 *
 * @param {string | undefined} status
 * @returns {boolean}
 */
export function msaInForce(status) {
    return status === MSA_EXECUTED || status === MSA_RECORDED;
}

/**
 * Collapse case and whitespace the way the API compares a typed signature
 * with the legal name it must match.
 *
 * @param {string} s
 * @returns {string}
 */
function normalizeName(s) {
    return (s || '').trim().split(/\s+/).join(' ').toLowerCase();
}

/**
 * Whether a typed signature is the signer's legal name, ignoring case and
 * extra spaces. Mirrors msa.SignatureMatches in the API.
 *
 * @param {string} typed
 * @param {string} legalName
 * @returns {boolean}
 */
export function signatureMatches(typed, legalName) {
    const t = normalizeName(typed);
    return t !== '' && t === normalizeName(legalName);
}

/**
 * The parties-block fields the contractor fills in, in form order, with the
 * lane's labels for region and postal code.
 *
 * @param {{ region_label?: string, postal_label?: string }} doc
 * @returns {{ key: string, label: string }[]}
 */
export function partiesFields(doc) {
    return [
        { key: 'legal_name', label: 'Full legal name' },
        { key: 'street', label: 'Street address' },
        { key: 'city', label: 'City' },
        { key: 'region', label: doc?.region_label || 'State or province' },
        { key: 'postal_code', label: doc?.postal_label || 'Postal code' },
    ];
}

/**
 * Whether every parties-block field has a value.
 *
 * @param {Record<string, string>} parties
 * @returns {boolean}
 */
export function partiesComplete(parties) {
    return partiesFields({}).every((f) => (parties?.[f.key] || '').trim() !== '');
}

/** The tax form each agreement lane goes with (W-9 for the US, W-8BEN for Canada). */
const TAX_FORMS = { us: 'W-9', ca: 'W-8BEN' };

/**
 * The tax form a contractor on this lane gives Dice Driven, or '' for an
 * unknown lane.
 *
 * @param {string | undefined} lane
 * @returns {string}
 */
export function taxFormFor(lane) {
    return TAX_FORMS[lane] ?? '';
}
